import { createContext, ReactNode, useCallback, useContext } from 'react';

import { FailedResp, Resp, SuccessResp, useApi } from '@paradigm/api';
import { useSubmitted } from '@paradigm/features/src/forms/submitted-context';

import { useGrfqRfq } from '#/unified-rfqs/ui/rfq-context';
import { CreateQuoteReport } from '#/unified-rfqs/ui/submit-quote';
import { Grfq, GrfqQuote } from '#/unified-rfqs/entities/grfq/domain';
import { getProduct } from '#/products/domain';
import { isDatedInstrument } from '#/unified-rfqs/ui/rfqs/legs-functions';

import { usePricingAlgoVersion } from '#/unified-rfqs/ui/hooks/shared';
import { parseClearingUnitiesToQty } from '#/unified-rfqs/ui/rfqs/quotes-functions';
import {
  useQuoteBuilderView,
  View,
} from '#/unified-rfqs/ui/grfq/quote-builder/view';
import {
  createOrReplacePricedQuotes,
  CreatePricedQuotesArgs,
} from '#/unified-rfqs/ui/grfq/quote-builder/submit-priced-quote';
import {
  useQuoteBuilderModalMethods,
  useQuoteBuilderModalState,
} from '#/unified-rfqs/ui/grfq/quote-builder/modal-context';
import { validateQuantityErrors } from '#/unified-rfqs/ui/grfq/quote-builder/views/quantity';
import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';

import { GrfqQuoteV2CreateResp } from '#/unified-rfqs/repositories/grfq-types';
import type { PricingAlgoVersion } from '#/unified-rfqs/entities/shared';
import type { JsonValue } from 'type-fest';

interface Success extends SuccessResp<CreateQuoteReport> {}

export interface SendCallback {
  (resp: Success | FailedResp): void;
}

interface QuoteBuilderSubmit {
  readonly isLoading: boolean;
  readonly submitQuote: (cb: SendCallback) => void;
}

const throwMissingProvider = () => {
  throw new Error('Missing QuoteBuilderSubmitProvider upwards in this tree');
};

const QuoteBuilderSubmitCtx = createContext<QuoteBuilderSubmit>({
  isLoading: false,
  submitQuote: throwMissingProvider,
});
QuoteBuilderSubmitCtx.displayName = 'QuoteBuilderSubmitCtx';

interface Props {
  readonly children: ReactNode;
}

export const QuoteBuilderSubmitProvider = ({ children }: Props) => {
  const grfqService = useGrfqService();
  const [{ isLoading }, createQuotes] = useApi(
    createOrReplacePricedQuotes(grfqService.createQuote),
  );
  const view = useQuoteBuilderView();
  const rfq = useGrfqRfq();
  const { setSubmit } = useSubmitted();
  const { setLegPricing } = useQuoteBuilderModalMethods();
  const { legPricingEnabled } = useQuoteBuilderModalState();
  const pricingAlgoVersion = usePricingAlgoVersion(rfq);

  const submitQuote = useCallback(
    (cb: SendCallback) => {
      if (!view.isValid) throw new TypeError(INVALID_ERROR_MESSAGE);

      const createQuotesReq = prepareCreateQuotesReq(
        view,
        rfq,
        legPricingEnabled,
        pricingAlgoVersion,
      );

      const product = getProduct(rfq.product_codes[0]!);
      const strategyLeg = rfq.legs.find(
        (leg) => leg.product_code === rfq.product_codes[0],
      );
      const qtyErrors = validateQuantityErrors(
        createQuotesReq.quantity,
        product,
        strategyLeg != null && isDatedInstrument(strategyLeg.instrument),
      );
      if (qtyErrors != null) {
        const errorResp = {
          ok: false,
          error: 'QuoteQuantityError',
          message: qtyErrors,
        } as FailedResp;
        cb(errorResp);
        return;
      }

      createQuotes(createQuotesReq, (resp) => {
        if (resp.error === 'LegPricingError') setLegPricing(true);
        // if submission was successful, don't display warning fields
        if (resp.ok && resp.data.response.ok) setSubmit(false);
        cb(processResp(resp));
      });
    },
    [
      view,
      rfq,
      legPricingEnabled,
      pricingAlgoVersion,
      createQuotes,
      setLegPricing,
      setSubmit,
    ],
  );

  const state: QuoteBuilderSubmit = { isLoading, submitQuote };

  return (
    <QuoteBuilderSubmitCtx.Provider value={state}>
      {children}
    </QuoteBuilderSubmitCtx.Provider>
  );
};

export const useQuoteBuilderSubmit = () => {
  return useContext(QuoteBuilderSubmitCtx);
};

function processResp(resp: Resp<CreateQuoteReport>): Resp<CreateQuoteReport> {
  if (!resp.ok) return resp;

  return {
    ...resp,
    data: {
      ...resp.data,
      response: processInnerResp(resp.data.response),
    },
  };
}

function processInnerResp(
  resp: Resp<GrfqQuote | GrfqQuoteV2CreateResp>,
): Resp<GrfqQuote | GrfqQuoteV2CreateResp> {
  if (resp.ok) return resp;

  const errorData = resp.data.data ?? {};

  const priceMsg = getMessageByKey(errorData, 'price');
  if (priceMsg != null)
    return { ...resp, error: 'LegPricingParameterError', message: priceMsg };

  return resp;
}

function getMessageByKey(data: Record<string, JsonValue>, key: string) {
  const maybeArray = data[key];
  const maybeMsg = Array.isArray(maybeArray) ? maybeArray[0] : maybeArray;
  return typeof maybeMsg === 'string' ? maybeMsg : null;
}

function prepareCreateQuotesReq(
  view: View,
  rfq: Grfq,
  legPricingEnabled: boolean,
  algorithmVersion: PricingAlgoVersion,
): CreatePricedQuotesArgs {
  const { id: rfqId, venue, legs, product_codes } = rfq;
  const { account, quantity, price, legsPrices, isPostOnly, side } = view;

  return {
    rfqId,
    venue,
    side,
    account: account!,
    price: price.value,
    quantity: parseClearingUnitiesToQty(quantity.value, product_codes[0]!).qty,
    legs,
    legsPrices,
    legPricingEnabled,
    isPostOnly,
    algorithmVersion,
  } as const;
}

const INVALID_ERROR_MESSAGE =
  'Quote builder state is not valid. Make sure to only call this function when the state is valid.';
